import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { editCompound, fetchCompounds } from "../../store/Host/compoundsSlice";
import { Compound, OfferCompoundService } from "../../services/restserver-openapi";

type CompoundFilters = {
    hostId: number;
    regionId: number;
    revised?: boolean;
    visible?: boolean;
    types: string[];
};

type Props = {
    filters: CompoundFilters;
};

const tableContainerSx = {
    borderRadius: 3,
    border: "none",
    borderColor: "divider",
    boxShadow: "none",
    overflow: "hidden",
    bgcolor: "background.paper",
} as const;

const headTypoSx = {
    textTransform: "uppercase",
    letterSpacing: 0.8,
} as const;

const CompoundTable = ({ filters }: Props) => {
    const dispatch = useAppDispatch();
    const compounds: Compound[] = useAppSelector((state) => state.compounds.compounds);
    const loading = useAppSelector((state) => state.compounds.loading);
    const [updatingRows, setUpdatingRows] = useState<Record<number, boolean>>({});
    const [optimisticFlags, setOptimisticFlags] = useState<Record<number, Partial<Pick<Compound, "revised" | "visibleCompound" | "editLockHoursBeforeArrival">>>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const handleBooleanUpdate = async (compound: Compound, key: "revised" | "visibleCompound", value: boolean) => {
        const compoundId = compound.compoundId;

        if (compound[key] === value || !compoundId) {
            return;
        }

        setUpdatingRows((prev) => ({ ...prev, [compoundId]: true }));
        setUpdateError(null);
        setOptimisticFlags((prev) => ({
            ...prev,
            [compoundId]: {
                ...prev[compoundId],
                [key]: value,
            },
        }));

        try {
            const fullCompound = await OfferCompoundService.getCompound(compoundId);
            await dispatch(
                editCompound({
                    ...fullCompound,
                    [key]: value,
                })
            ).unwrap();
        } catch (error) {
            console.error("Error while updating compound flag", error);
            setUpdateError("Update fehlgeschlagen. Bitte erneut versuchen.");
            setOptimisticFlags((prev) => ({
                ...prev,
                [compoundId]: {
                    ...prev[compoundId],
                    [key]: compound[key],
                },
            }));
        } finally {
            setUpdatingRows((prev) => ({ ...prev, [compoundId]: false }));
        }
    };

    const handleEditLockHoursChange = (compoundId: number, value: string) => {
        const parsedValue = Number(value);

        setOptimisticFlags((prev) => ({
            ...prev,
            [compoundId]: {
                ...prev[compoundId],
                editLockHoursBeforeArrival: Number.isFinite(parsedValue) ? parsedValue : undefined,
            },
        }));
    };

    const handleEditLockHoursUpdate = async (compound: Compound) => {
        const compoundId = compound.compoundId;

        if (!compoundId) {
            return;
        }

        const optimisticValue = optimisticFlags[compoundId]?.editLockHoursBeforeArrival;
        const currentValue = compound.editLockHoursBeforeArrival ?? 48;
        const nextValue = optimisticValue ?? currentValue;

        if (!Number.isFinite(nextValue) || nextValue <= 0) {
            setUpdateError("Edit-Lock muss eine positive Zahl sein.");
            setOptimisticFlags((prev) => ({
                ...prev,
                [compoundId]: {
                    ...prev[compoundId],
                    editLockHoursBeforeArrival: currentValue,
                },
            }));
            return;
        }

        if (nextValue === currentValue) {
            return;
        }

        setUpdatingRows((prev) => ({ ...prev, [compoundId]: true }));
        setUpdateError(null);

        try {
            const fullCompound = await OfferCompoundService.getCompound(compoundId);
            await dispatch(
                editCompound({
                    ...fullCompound,
                    editLockHoursBeforeArrival: nextValue,
                })
            ).unwrap();
        } catch (error) {
            console.error("Error while updating edit lock hours", error);
            setUpdateError("Update fehlgeschlagen. Bitte erneut versuchen.");
            setOptimisticFlags((prev) => ({
                ...prev,
                [compoundId]: {
                    ...prev[compoundId],
                    editLockHoursBeforeArrival: currentValue,
                },
            }));
        } finally {
            setUpdatingRows((prev) => ({ ...prev, [compoundId]: false }));
        }
    };

    useEffect(() => {
        dispatch(
            fetchCompounds({
                hostId: filters.hostId,
                regionId: filters.regionId,
                revised: filters.revised,
                visible: filters.visible,
                types: filters.types,
            })
        );
    }, [dispatch, filters]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(compounds.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [compounds.length, page, rowsPerPage]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    const paginatedCompounds = compounds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">Lade Compounds…</Typography>
            </Box>
        );
    }

    return (
        <>
            {updateError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {updateError}
                </Alert>
            )}
            <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
                <Table stickyHeader>
                <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "background.paper", borderBottomColor: "divider" } }}>
                        {["Id", "Name", "HostId", "Typ", "Edit-Lock (h)", "Revised", "Visible", "Page"].map((h) => (
                            <TableCell key={h}>
                                <Typography
                                    color="text.secondary"
                                    variant="caption"
                                    fontWeight={900}
                                    sx={headTypoSx}
                                >
                                    {h}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {paginatedCompounds.map((compound) => (
                        <TableRow
                            key={compound.compoundId}
                            hover
                            sx={{
                                // "& td": { borderBottomColor: "divider" },
                                // "&:nth-of-type(2n)": { bgcolor: "alternate.main" },
                            }}
                        >
                            <TableCell>{compound.compoundId}</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>{compound.name}</TableCell>
                            <TableCell>{compound.hostId}</TableCell>
                            <TableCell>{compound.compoundType}</TableCell>

                            <TableCell sx={{ minWidth: 110 }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={optimisticFlags[compound.compoundId ?? -1]?.editLockHoursBeforeArrival ?? compound.editLockHoursBeforeArrival ?? 48}
                                    onChange={(event) => handleEditLockHoursChange(compound.compoundId ?? -1, event.target.value)}
                                    onBlur={() => handleEditLockHoursUpdate(compound)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            handleEditLockHoursUpdate(compound);
                                        }
                                    }}
                                    inputProps={{ min: 1, step: 1 }}
                                    disabled={!compound.compoundId || updatingRows[compound.compoundId] === true}
                                    sx={{ width: 80 }}
                                />
                            </TableCell>

                            <TableCell align="center">
                                <Checkbox
                                    checked={(optimisticFlags[compound.compoundId ?? -1]?.revised ?? compound.revised) === true}
                                    onChange={(_, checked) => handleBooleanUpdate(compound, "revised", checked)}
                                    icon={<DisabledByDefaultIcon color="error" />}
                                    checkedIcon={<CheckBoxIcon color="success" />}
                                    disabled={!compound.compoundId || updatingRows[compound.compoundId] === true}
                                />
                            </TableCell>

                            <TableCell align="center">
                                <Checkbox
                                    checked={(optimisticFlags[compound.compoundId ?? -1]?.visibleCompound ?? compound.visibleCompound) === true}
                                    onChange={(_, checked) => handleBooleanUpdate(compound, "visibleCompound", checked)}
                                    icon={<DisabledByDefaultIcon color="error" />}
                                    checkedIcon={<CheckBoxIcon color="success" />}
                                    disabled={!compound.compoundId || updatingRows[compound.compoundId] === true}
                                />
                            </TableCell>

                            <TableCell>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<InfoIcon />}
                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                    onClick={() => window.open(`/vanlifer/Campsite/${compound.compoundId}`, "_blank", "noopener,noreferrer")}
                                >
                                    Details
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}

                    {compounds.length === 0 && (
                        <TableRow sx={{ "& td": { borderBottomColor: "divider" } }}>
                            <TableCell colSpan={8}>
                                <Typography color="text.secondary">Keine Resultate für diese Filter.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={compounds.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>
        </>
    );
};

export default CompoundTable;
